/*
 * Copyright (c) 2008-2025 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

import { LICENSE_FEATURES } from "@/config/licensing_constants"
import { PERMISSIONS } from "@/config/permissions"
import {
  ifNotAuthenticated,
  beforeRoutePermissionCheckGuard,
  beforeRouteLicenseCheckGuard,
  beforeRouteEitherLicenseAndPermissionCheckGuard,
} from "@/utils/BeforeRouteCheckGuards"
import Vue from "vue"
import Router from "vue-router"
import { ensureLicenseLoaded } from "./licensing/licenseValidation"
import store from "./store"

Vue.use(Router)

/* CREATE GENERAL ROUTER INSTANCE */

const router = new Router({
  // Use URLs without hash sign (DEV-1291)
  mode: "history",
  base: "/control",
  routes: [
    {
      path: "/login",
      name: "authRequired",
      component: () => import(/* webpackChunkName: "authRequired" */ "@/components/AuthRequired"),
      beforeEnter: ifNotAuthenticated,
    },
    { path: "/", name: "home", redirect: "/meetings" },
    {
      path: "/meetings",
      name: "meetings",
      component: () => import(/* webpackChunkName: "meetingList" */ "@/components/MeetingList"),
    },

    // IMPORTANT: communication/index.js adds a beforeEach hook to the router that watches for navigation with name
    //            starting with "cockpit" to start / stop the communication
    {
      path: "/meetings/:id",
      component: () => import(/* webpackChunkName: "cockpitLayout" */ "@/components/cockpit/CockpitLayout"),
      props: true,
      children: [
        {
          path: "info",
          name: "cockpitInfo",
          component: () => import(/* webpackChunkName: "cockpitInfo" */ "@/components/cockpit/CockpitInfo"),
          beforeEnter: beforeRoutePermissionCheckGuard(PERMISSIONS.VIEW_MEETING),
          props: true,
        },
        {
          path: "participants",
          name: "cockpitParticipants",
          component: () =>
            import(/* webpackChunkName: "cockpitParticipants" */ "@/components/cockpit/CockpitParticipants"),
          beforeEnter: beforeRoutePermissionCheckGuard(PERMISSIONS.VIEW_PARTICIPANT_USER),
          props: true,
        },
        {
          path: "script",
          name: "cockpitScript",
          component: () => import(/* webpackChunkName: "cockpitScript" */ "@/components/cockpit/CockpitScript"),
          beforeEnter: beforeRoutePermissionCheckGuard(PERMISSIONS.VIEW_INTERACTION),
          props: true,
        },
        {
          path: "result",
          name: "cockpitResult",
          component: () => import(/* webpackChunkName: "cockpitResult" */ "@/components/cockpit/CockpitResult"),
          beforeEnter: beforeRouteEitherLicenseAndPermissionCheckGuard(
            LICENSE_FEATURES.MEETING_RESULTS,
            LICENSE_FEATURES.TIME_TRACKING,
            PERMISSIONS.VIEW_EXPORT
          ),
          props: true,
        },
      ],
    },
    {
      path: "/tenants",
      name: "tenantManagement",
      component: () => import(/* webpackChunkName: "tenantManagement" */ "@/components/admin/TenantList"),
    },
    {
      path: "/users",
      name: "userManagement",
      component: () => import(/* webpackChunkName: "userManagement" */ "@/components/admin/UserList"),
      beforeEnter: beforeRouteLicenseCheckGuard(LICENSE_FEATURES.MULTI_USER),
    },
    {
      path: "/licensing",
      name: "licenseManagement",
      component: () => import(/* webpackChunkName: "licenseManagement" */ "@/components/admin/LicenseList"),
    },
    {
      path: "/lan-event",
      name: "lanEvent",
      component: () => import(/* webpackChunkName: "lanEvent" */ "@/components/admin/LanEvent"),
    },
    {
      path: "/maintenance-message",
      name: "maintenanceMessage",
      component: () => import(/* webpackChunkName: "maintenanceMessage" */ "@/components/admin/MaintenanceMessage"),
    },
    {
      path: "/server_statistics",
      name: "serverStatistics",
      component: () => import(/* webpackChunkName: "serverStatistics" */ "@/components/admin/ServerStatistics"),
    },
    {
      path: "/server-license",
      name: "serverLicense",
      component: () => import(/* webpackChunkName: "serverLicense" */ "@/components/admin/ServerLicense"),
    },
    {
      path: "/agenda",
      name: "agenda",
      component: () => import(/* webpackChunkName: "agendaUpload" */ "@/components/cms/AgendaUpload"),
    },
    {
      path: "/speakerInfo",
      name: "speaker",
      component: () => import(/* webpackChunkName: "speakerUpload" */ "@/components/cms/SpeakerUpload"),
    },
    {
      path: "/travelInfo",
      name: "travelInfo",
      component: () => import(/* webpackChunkName: "travelInfoUpload" */ "@/components/cms/TravelInfoUpload"),
    },
    {
      path: "/keyVisuals",
      name: "lockScreenImage",
      component: () => import(/* webpackChunkName: "lockScreenImageUpload" */ "@/components/cms/LockScreenImageUpload"),
    },
    {
      path: "/genericImages",
      name: "genericImage",
      component: () => import(/* webpackChunkName: "genericImageUpload" */ "@/components/cms/GenericImageUpload"),
    },
    {
      path: "/*",
      redirect: "/",
    },
  ],
})

/* BEFORE HOOKS */

router.beforeEach((to, from, next) => {
  ensureLicenseLoaded().then(() => {
    // Ensure maintenance message is updated
    store.dispatch("settings/loadMaintenanceMessage")

    next()
  })
})

export default router
